/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useQuery } from 'utils/common';
import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import EVENTS from 'constants/analytics';
import Analytics from 'utils/analytics';
import Loader from 'components/Loader';
import {
  Conditional,
  Toggle,
  Space,
  Tab,
  AlertModal,
  Popup,
} from '@cashfree-intl/coherent';
import CustomIcon from '../../components/CustomIcons';
import makeSelectPreferences from './selectors';
import reducer from './reducer';
import saga from './saga';
import {
  fetchPreferencesAction,
  addPreferencesAction,
  updatePreferencesAction,
  updateGroupPreferencesAction,
  updateShowRecepientAddedSuccessModal as updateShowRecipientAddedSuccessModalAction,
} from './actions';
import { items } from './constants';
import { ICommPreferences } from './types';
import {
  PreferenceCard,
  PreferenceChips,
  PreferenceValue,
  StyledLinkButton
} from './styled';
import RemovePopup from './components/RemovePopup';
import AddPopup from './components/AddPopup';

export function CommunicationPreferences({
  communicationPreferences: { showRecipientAddedSuccessModal },
  fetchPreferences,
  addPreferences,
  updatePreferences,
  updateGroupPreferences,
  updateShowRecipientAddedSuccessModal,
  isCommsPrefEnabled = false
}) {
  useInjectReducer({ key: 'communicationPreferences', reducer });
  useInjectSaga({ key: 'communicationPreferences', saga, mode: '' });

  const query = useQuery();
  const env = query.get('env');

  const [isLoading, resetLoading] = useState(false);

  const [addPopup, setAddPopup] = useState({
    show: false,
    category: '',
    preference: '',
  });

  const [removePopup, setRemovePopup] = useState({
    show: false,
    item: {},
    preference: '',
    value: '',
  });

  const [communicationPreferences, setCommunicationPreferences] = useState({
    channel_details: {},
    merchant_config: {},
  });

  const [commInfo, setCommInfo] = useState({
    API_UPDATES: {
      commTab: -1,
      expand: false,
    },
    SDK_UPDATES: {
      commTab: -1,
      expand: false,
    },
    REALTIME_API_ERROR_ALERT: {
      commTab: -1,
      expand: false,
    },
  });

  useEffect(() => {
    resetLoading(true);
    getCommunications();
    Analytics.track(EVENTS.COMMUNICATION_PREFERENCES.LOAD);
  }, [env]);

  const getCommunications = () => {
    fetchPreferences({
      successCb: (response: any) => {
        setCommunicationPreferences(response);
        resetLoading(false);
      },
      errorCb: () => resetLoading(false),
    });
  };

  const addCommunications = (
    category: string,
    value: string,
    preference: string,
  ) => {
    if (
      communicationPreferences.channel_details[
        (items.find(item => item.key === category)?.key)
      ].filter((channel: any) => channel.channelValue === value).length
    ) {
      updateCommunications(
        items.find(item => item.key === category),
        preference,
        value,
        true,
      );
    } else {
      addPreferences({
        reqOptions: {
          notificationType: category,
          notificationChannel: preference,
          channelValue: value,
          isActive: true,
        },
        successCb: () => {
          updateShowRecipientAddedSuccessModal(true);
          resetLoading(false);
          getCommunications();
        },
        errorCb: () => {
          resetLoading(false);
        },
        env,
      });
    }
  };

  const updateCommunications = (
    item: any,
    channel: string,
    value: string,
    active: boolean,
  ) => {
    resetLoading(true);

    updatePreferences({
      reqOptions: {
        notificationType: item.key,
        notificationChannel: channel,
        isActive: active,
        channelValue: value,
      },
      successCb: () => {
        getCommunications();
        resetLoading(false);
      },
      errorCb: () => resetLoading(false),
    });
  };

  const updateGroupCommunications = (
    key: string,
    channel: string,
    active: boolean,
  ) => {
    resetLoading(true);
    updateGroupPreferences({
      reqOptions: {
        notificationType: key,
        notificationChannel: channel,
        isActive: active,
      },
      successCb: () => {
        getCommunications();
        resetLoading(false);
      },
      errorCb: () => resetLoading(false),
    });
  };

  const onAddSubmit = values => {
    resetLoading(true);
    setAddPopup({
      show: false,
      category: '',
      preference: '',
    });
    addCommunications(
      values.categoryValue,
      values.emailValue,
      values.preferenceValue,
    );
    Analytics.track(EVENTS.COMMUNICATION_PREFERENCES.ADD, {
      type: 'email',
    });
  };

  return (
    <div className="coummunication-container">
      <Loader active={isLoading} />
      {Object.keys(communicationPreferences).length > 0 && (
        <div className="communication-channels mt-2">
          {items.map(item => (
            <PreferenceCard>
              <Space
                className="pointer"
                justifyContent="space-between"
                alignItems="center"
                onClick={() => {
                  setCommInfo(prevCommInfo => ({
                    ...prevCommInfo,
                    [item.key]: {
                      commTab: 0,
                      expand: !prevCommInfo[item.key].expand,
                    },
                  }));
                }}
              >
                <div>
                  <div className="email-title medium-text text-dark w-600">
                    {item.title}
                  </div>
                  <div className="medium-text text-light pt-1">
                    <Conditional if={isCommsPrefEnabled}>
                    This feature has been moved to new Communications Page. <StyledLinkButton
                      as="a"
                      link
                      href={`${
                        process.env.COMMON_BASE_PATH
                      }/profile/communication-preference?tabGroup=Updates+and+Alerts`}
                    >
                      Click here to access new page
                    </StyledLinkButton>
                    </Conditional>
                    <Conditional if={!isCommsPrefEnabled}>
                    {item.text}
                    </Conditional>
                       </div>
                </div>
                {!isCommsPrefEnabled &&
                  <Space gap={3}>
                    <Toggle
                      active={communicationPreferences.merchant_config[item.key]}
                      onToggle={(e: any) => {
                        e.stopPropagation();
                        updateGroupCommunications(
                          item.key,
                          'EMAIL',
                          !communicationPreferences.merchant_config[item.key],
                          );
                        }}
                        />
                    <span className="align-center">
                      <CustomIcon
                        componentName="CHEVRONDOWN"
                        direction={commInfo[item.key]?.expand ? 'up' : 'down'}
                        />
                    </span>
                  </Space>}
              </Space>
              <Conditional if={!isCommsPrefEnabled && commInfo[item.key].expand}>
                <Tab
                  activeIndex={commInfo[item.key].commTab}
                  menu={{ secondary: true, pointing: true }}
                  panes={item.panes}
                  onTabChange={(e: any, data: any) => {
                    setCommInfo(prevCommInfo => ({
                      ...prevCommInfo,
                      [item.key]: {
                        ...prevCommInfo[item.key],
                        commTab: data.activeIndex,
                      },
                    }));
                  }}
                  className="mt-2"
                />
                {commInfo[item.key].commTab === 0 && (
                  <PreferenceChips>
                    {communicationPreferences.channel_details[(item?.key)]
                      .filter(
                        (channelItem: any) =>
                          channelItem.isActive === true &&
                          channelItem.notificationChannel === 'EMAIL',
                      )
                      .map((channel: any) => (
                        <Popup
                          position="top center"
                          content={
                            channel?.status === 'VERIFIED'
                              ? 'Recipient Approved'
                              : 'Recipient Approval Pending'
                          }
                          trigger={
                            <PreferenceValue
                              verified={channel?.status === 'VERIFIED'}
                            >
                              <span className="pr-1">
                                <CustomIcon componentName="GREEN_TICK" />
                              </span>
                              {channel.channelValue}
                              <span
                                className="close pointer pl-1"
                                onClick={() => {
                                  setRemovePopup({
                                    show: true,
                                    item,
                                    preference: 'EMAIL',
                                    value: channel.channelValue,
                                  });
                                }}
                              >
                                <CustomIcon
                                  componentName="CLOSE"
                                  fill={
                                    channel?.status === 'VERIFIED'
                                      ? '#fff'
                                      : '#000'
                                  }
                                />
                              </span>
                            </PreferenceValue>
                          }
                        />
                      ))}

                    {communicationPreferences.merchant_config[item.key] ? (
                      <PreferenceValue
                        onClick={() => {
                          setAddPopup({
                            show: true,
                            category: item.key,
                            preference:
                              item.panes[commInfo[item.key].commTab].key,
                          });
                        }}
                      >
                        <span className="pr-1">
                          <CustomIcon componentName="ADD_BUTTON" />
                        </span>
                        Add
                      </PreferenceValue>
                    ) : (
                      'Enable Toggle Preferences to add or view data'
                    )}
                  </PreferenceChips>
                )}
                {commInfo[item.key].commTab === 1 && (
                  <PreferenceChips>Coming Soon!!</PreferenceChips>
                )}
                {commInfo[item.key].commTab === 2 && (
                  <PreferenceChips>Coming Soon!!</PreferenceChips>
                )}
              </Conditional>
            </PreferenceCard>
          ))}
        </div>
      )}
      <Conditional if={addPopup.show}>
        <AddPopup
          onCloseClick={() => {
            setAddPopup({
              show: false,
              category: '',
              preference: '',
            });
          }}
          onSubmit={onAddSubmit}
          info={addPopup}
        />
      </Conditional>

      <Conditional if={removePopup.show}>
        <RemovePopup
          onCloseClick={() => {
            setRemovePopup({
              show: false,
              item: {},
              preference: '',
              value: '',
            });
          }}
          onConfirmClick={() => {
            updateCommunications(
              removePopup.item,
              removePopup.preference,
              removePopup.value,
              false,
            );
            setRemovePopup({
              show: false,
              item: {},
              preference: '',
              value: '',
            });
          }}
          value={removePopup.value}
        />
      </Conditional>

      <Conditional if={showRecipientAddedSuccessModal}>
        <AlertModal
          type="success"
          title="Recipient Added Successfully"
          onClose={() => updateShowRecipientAddedSuccessModal(false)}
          eventNameOnClose="Show_Recipient_Added_Success_Close"
        >
          An email notification will be sent to the recipient confirming their
          approval.
        </AlertModal>
      </Conditional>
    </div>
  );
}

CommunicationPreferences.propTypes = {
  fetchPreferences: PropTypes.func.isRequired,
  addPreferences: PropTypes.func.isRequired,
  updatePreferences: PropTypes.func.isRequired,
  updateGroupPreferences: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  communicationPreferences: makeSelectPreferences(),
});

function mapDispatchToProps(dispatch: any) {
  return {
    fetchPreferences: (data: ICommPreferences) =>
      dispatch(fetchPreferencesAction(data)),
    addPreferences: (data: ICommPreferences) =>
      dispatch(addPreferencesAction(data)),
    updatePreferences: (data: ICommPreferences) =>
      dispatch(updatePreferencesAction(data)),
    updateGroupPreferences: (data: ICommPreferences) =>
      dispatch(updateGroupPreferencesAction(data)),
    updateShowRecipientAddedSuccessModal: (data: ICommPreferences) =>
      dispatch(updateShowRecipientAddedSuccessModalAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(CommunicationPreferences);
